import { ZodAdAttributionSchema } from "@billingnow/zod-schema";
import { apiClient } from "./client";

/**
 * Delete the authenticated user's account and all associated data
 */
export const deleteAccount = async (): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>("/api/account");
  return response.data;
};

/**
 * Record the furthest onboarding step the user reached (admin funnel analytics)
 */
export const updateOnboardingStep = async (step: number): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>("/api/account/onboarding-step", { step });
  return response.data;
};

/**
 * Record the furthest funnel position the user reached (per user, not per
 * session; wizard tier is forward-only). Clients may only report wizard steps
 * and "paywall" — "trial"/"active"/"canceled" come from the subscription
 * webhooks.
 */
export const updateUserStatus = async (
  status: "step1" | "step2" | "step3" | "step4" | "step5" | "paywall",
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>("/api/account/status", { status });
  return response.data;
};

/**
 * Store ad click ids + marketing consent for server-side conversion tracking.
 * Fire-and-forget after sign-in.
 */
export const saveAdAttribution = async (data: ZodAdAttributionSchema): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>("/api/account/ad-attribution", data);
  return response.data;
};

/**
 * Whether the user has a password they actually chose. False for OAuth /
 * magic-link users and legacy random-password signups — those see "Set
 * password" instead of "Change password".
 */
export const getPasswordStatus = async (): Promise<{ hasPassword: boolean }> => {
  const response = await apiClient.get<{ hasPassword: boolean }>("/api/account/password-status");
  return response.data;
};

/**
 * Set or change the account password. `currentPassword` is required only when
 * the user already has a chosen password.
 */
export const updatePassword = async (data: {
  newPassword: string;
  currentPassword?: string;
}): Promise<{ hasPassword: boolean }> => {
  const response = await apiClient.post<{ hasPassword: boolean }>("/api/account/password", data);
  return response.data;
};
